import Editor from "@monaco-editor/react";
import { IScheduledTaskHistoryDTO, ScheduledTasksApi } from "api/ScheduledTasksApi";
import { ScheduledTaskDTO } from "api/model/airflow-task/ScheduledTaskDTO";
import { TaskInputGroup } from "api/model/airflow-task/TaskInputGroup";
import {
  ITaskTemplateInputDefinition,
  TaskTemplateDefinitionDTO,
  TaskTemplateInputGroupDefinition,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import {
  TaskTemplateInputGroupType,
  TaskTemplateInputType,
  TaskTemplatingType,
} from "api/model/enums";
import CreateDatasetDialog from "components/dashboard/dataset-window/datasets-menu/create-dataset-dialog/CreateDatasetDialog";
import InfoLabel from "components/pages/scheduled-task-airflow/components/InfoLabel";
import TaskConfigConstantMapTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigConstantMapTable";
import TaskConfigIndexedDatasetTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigIndexedDatasetTable";
import TaskConfigNamedDatasetTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigNamedDatasetTable";
import TaskConfigProductTemplatesTable from "components/pages/scheduled-task-airflow/components/tables/TaskConfigProductTemplatesTable";
import EasasPageTemplateWrapper from "components/templates/EasasPageTemplateWrapper";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Menu } from "primereact/menu";
import { TabPanel, TabView } from "primereact/tabview";
import { Tag } from "primereact/tag";
import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { taskExecutorQueues } from "utils/constants";
import { formatDatetimeDefault } from "utils/time-utils";
import { ScheduledTaskHistoryTable } from "../../basic/tables/scheduled-task-history/ScheduledTaskHistoryTable";
import UserAvatar from "../../basic/user-avatar/UserAvatar";
import { easasAlert } from "../../fragments/easas-global-fragment/EasasGlobalAlerts";
import "./AirflowTaskConfigurator.scss";
import {
  BooleanFieldInput,
  ChipsFieldInput,
  CodeUploadInput,
  DatasetCategoryDropdownField,
  DatasetDropdownFieldInput,
  DatasetMultiSelectFieldInput,
  DropdownFieldInput,
  IFieldInputProps,
  InputChangeEvent,
  IPythonCodeFile,
  NumberFieldInput,
  TaskConfigConstantField,
  TaskConfigFreestyleJsonField,
  TaskConfigProductTemplateField,
  TaskDependencyDropdown,
  TextFieldInput,
  TimespanFieldInput,
} from "./components/InputFields";
import { TaskDataDownloadButton } from "./components/TaskDataDownloadButton";
import TemplateInfoDialog from "./components/TemplateInfoDialog";
import TemplateUpdateDialog from "./components/TemplateUpdateDialog";
import TaskConfigCategoryTable from "./components/tables/TaskConfigCategoryTable";
import TaskConfigDatasetPairTable from "./components/tables/TaskConfigDatasetPairTable";
import TaskTemplatesSubgroupManagerTable from "./components/tables/TaskTemplatesSubgroupManagerTable";
import InputComment from "./components/InputComment/InputComment";
import TemplateEditor from "./components/templateEditor/TemplateEditor";

export default function AirflowTaskConfigurator() {
  const [taskCfg, setTaskCfg] = useState<ScheduledTaskDTO>(new ScheduledTaskDTO());
  const [taskCfgLoading, setTaskCfgLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedTaskHistory, setSelectedTaskHistory] = useState<IScheduledTaskHistoryDTO>();
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [showTemplateInfoDialog, setShowTemplateInfoDialog] = useState(false);
  const [showTemplateUpdateDialog, setShowTemplateUpdateDialog] = useState(false);
  const [createDatasetDialogVisibility, setCreateDialogVisibility] = useState<boolean>(false);

  const menu = useRef<Menu>(null);

  const isTemplateForced = taskCfg.templateInfo.type === "PREDEFINED";
  const isTemplated = taskCfg.templateInfo.type === "USER_CUSTOM" || isTemplateForced;

  const menuItems = [
    {
      label: "Create dataset",
      icon: "pi pi-plus-circle",
      command: () => setCreateDialogVisibility(true),
    },
    {
      label: "Rollback to...",
      icon: "pi pi-history",
      command: () => setShowRollbackDialog(true),
    },
    {
      label: "Template info...",
      icon: "pi pi-info-circle",
      command: () => setShowTemplateInfoDialog(true),
    },
    {
      label: "Update template...",
      icon: "pi pi-refresh",
      command: () => setShowTemplateUpdateDialog(true),
    },
  ];

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dagId = params.get("dagId");
    const taskId = params.get("taskId");

    if (taskId == null || dagId == null) {
      throw Error("Invalid task editor URL options!");
    }

    ScheduledTasksApi.getAirflowTaskConfig(dagId, taskId).then(r => {
      if (!r.error) {
        setTaskCfg(r.data!!);
        setTaskCfgLoading(false);
      } else {
        setTaskCfgLoading(false);
        console.log(`error${r.error}`);
      }
    });
  }, []);

  useEffect(() => {
    console.log(taskCfg);
  }, [taskCfg]);

  const showSuccess = (message: string) => {
    easasAlert({ summary: "Success", detail: message, severity: "success" });
  };

  const showError = (message: string) => {
    easasAlert({ summary: "Error", detail: message, severity: "error" });
  };

  const showWarn = (message: string) => {
    easasAlert({ summary: "Warning", detail: message, severity: "warn" });
  };

  function setDefinition(e: TaskTemplateDefinitionDTO) {
    const newCfg = new ScheduledTaskDTO(taskCfg);
    newCfg.templateInfo.definition = e;
    setTaskCfg(newCfg);
  }

  const setInputPropertyState = (property: keyof ScheduledTaskDTO, value: any) => {
    setTaskCfg(
      prev =>
        new ScheduledTaskDTO({
          ...prev,
          [property]: value,
        } as any)
    );
  };

  const handleBasicInputChange = (e: InputChangeEvent<any>) => {
    setInputPropertyState(e.property as keyof ScheduledTaskDTO, e.value);
  };

  const toggleIsUserTemplated = (e: InputChangeEvent<boolean>) => {
    console.log("toggleIsUserTemplated", e);
    const type: TaskTemplatingType = e.value ? "USER_CUSTOM" : "NONE";
    const newCfg = new ScheduledTaskDTO(taskCfg);
    newCfg.templateInfo.type = type;
    setTaskCfg(newCfg);
  };

  const handleJsonEditorChange = (e: string | undefined) => {
    setInputPropertyState("freestyleJson", e);
  };

  const handleCodeFileChange = (e: IPythonCodeFile) => {
    setInputPropertyState("codeFileId", e.id);
    setInputPropertyState("codeFileName", e.name);
    setInputPropertyState("code", e.code);
    setInputPropertyState("codeB64", e.codeB64);
  };

  const handleTemplateGroupsChange = (e: Record<string, TaskInputGroup[]>) => {
    console.log("Set input groups", e);
    setInputPropertyState("inputGroups", e);
  };

  const openAirflowTab = () => {
    ScheduledTasksApi.getAirflowTaskGraphLink(taskCfg?.parentId!!, taskCfg?.id!!).then(r => {
      if (!r.error) {
        window.open(r.data!!, "_blank");
      } else {
        showError("Error while opening airflow graph window. Maybe not using SSO session?");
      }
    });
  };

  const handleSave = (run: boolean, onSaved?: () => void) => {
    console.log("save");
    setSaving(true);

    ScheduledTasksApi.saveAirflowTaskConfig(taskCfg, run).then(r => {
      if (!r.error) {
        console.log("saved");
        if (run) {
          showSuccess("Task successfully saved and started!");
        } else {
          showSuccess("Task configuration saved successfully!");
        }

        if (onSaved != null) {
          setTimeout(onSaved, 1000);
        }
      } else {
        console.error(r.error.message);
        showError(`Error saving task configuration! ${r.error.message}`);
      }

      setSaving(false);
    });
  };

  const handleRollback = () => {
    console.log("rollback");
    console.log(selectedTaskHistory);

    if (selectedTaskHistory == null) {
      showWarn("Please select a task history to rollback to!");
      return;
    }

    const parsedConfig = JSON.parse(selectedTaskHistory?.config);
    const cfg = new ScheduledTaskDTO(parsedConfig);

    setTaskCfg(cfg);
    setShowRollbackDialog(false);
  };

  const onClickSave = () => {
    handleSave(false, onClickGoBack);
  };

  const onClickApply = () => {
    handleSave(false);
  };

  const onClickRun = () => {
    handleSave(true);
  };

  const onClickGoBack = () => {
    // eslint-disable-next-line no-restricted-globals
    history.back();
  };

  const rollbackDialogFooter = () => {
    return (
      <div className="flex justify-content-between">
        <Button label="Confirm rollback" icon="pi pi-check" onClick={handleRollback} />
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => setShowRollbackDialog(false)}
          className="p-button-danger"
        />
      </div>
    );
  };

  return (
    <EasasPageTemplateWrapper
      breadcrumb={["Airflow", "Task Config"]}
      title="Scheduled Task Config"
      cardClass="flex"
      cardStyle={{
        maxWidth: "1000px",
        margin: "auto",
        height: "calc(100% - 1rem)",
      }}
    >
      <Tooltip id="task-title-tooltip" place="top" float />
      <div
        className="flex flex-column justify-content-between python-task-config"
        style={{ width: "100%" }}
      >
        <div>
          <div className="flex justify-content-between">
            <h2
              style={{ margin: "0 !important" }}
              data-tooltip-id="task-title-tooltip"
              data-tooltip-content={taskCfg.title}
              className="align-self-center overflow-hidden text-overflow-ellipsis white-space-nowrap"
            >
              {taskCfg.title}
            </h2>
            {isTemplateForced && (
              <Tag style={{ fontSize: "1rem" }} className="ml-4 align-self-center">
                {taskCfg.templateInfo.templateId}:{taskCfg.templateInfo.templateVersion}
              </Tag>
            )}
            <div className="flex align-content-center flex-nowrap white-space-nowrap">
              {taskCfg.lastUpdatedBy && (
                <span className="flex align-items-center mr-4">
                  <span className="flex flex-column align-items-end pr-2 text-sm">
                    <span>Last edited at</span>
                    <span>{formatDatetimeDefault(taskCfg.lastUpdated)}</span>
                  </span>

                  <UserAvatar username={taskCfg.lastUpdatedBy} />
                </span>
              )}

              <TaskDataDownloadButton taskCfg={taskCfg} />

              <Button
                icon="airflow-icon"
                className="p-button-info p-button-outlined ml-2"
                onClick={openAirflowTab}
              />

              <Menu model={menuItems} popup ref={menu} id="popup_menu" />
              <Button
                icon="pi pi-ellipsis-v"
                className="p-button-info ml-2"
                onClick={menu.current?.toggle}
              />
            </div>
          </div>

          {taskCfgLoading && <div>Loading...</div>}

          {!taskCfgLoading && (
            <TabView activeIndex={0} panelContainerStyle={{ paddingTop: "0" }} scrollable>
              <TabPanel header="General" contentClassName="mt-3">
                <div className="p-fluid grid formgrid">
                  <div className="col-12">
                    <h4>Options</h4>
                    <hr />
                  </div>

                  <TextFieldInput
                    label="Task Id"
                    property="id"
                    value={taskCfg?.id}
                    widthMd={4}
                    disabled
                  />

                  <TextFieldInput
                    label="DAG Id"
                    property="parentId"
                    value={taskCfg?.parentId}
                    widthMd={4}
                    disabled
                  />

                  <TextFieldInput
                    label="Company Identifier"
                    property="companyRef"
                    value={taskCfg?.companyRef}
                    widthMd={4}
                    disabled
                  />

                  <TextFieldInput
                    label="Title"
                    property="title"
                    value={taskCfg?.title}
                    widthMd={4}
                    onChange={handleBasicInputChange}
                  />

                  <TextFieldInput
                    label="Description"
                    info="Task's description"
                    property="description"
                    value={taskCfg?.description}
                    widthMd={8}
                    onChange={handleBasicInputChange}
                  />

                  <NumberFieldInput
                    label="Timeout"
                    info="Timeout specified in seconds, after this task will be killed."
                    property="timeout"
                    value={taskCfg?.timeout}
                    onChange={handleBasicInputChange}
                    widthMd={12}
                    suffix="s"
                  />

                  <TextFieldInput
                    label="Environment ID"
                    info="Environment to run task in (dependencies)"
                    property="environmentId"
                    value={taskCfg?.environmentId}
                    onChange={handleBasicInputChange}
                    widthMd={4}
                  />

                  <DropdownFieldInput
                    label="Executor queue"
                    info="In which queue execute task in. Queues have different specifications: some are faster, some are slower, other have specific requirements"
                    property="executorQueue"
                    value={taskCfg?.executorQueue}
                    onChange={handleBasicInputChange}
                    options={taskExecutorQueues}
                    widthMd={4}
                  />

                  <NumberFieldInput
                    label="Queue Slots"
                    info="Slots taken in queue pool. Used to limit concurrent task count in queues"
                    property="queueSlots"
                    value={taskCfg?.queueSlots}
                    onChange={handleBasicInputChange}
                    widthMd={4}
                  />

                  <TaskDependencyDropdown
                    label="Downstream Tasks"
                    info="Tasks that will be executed after this task successfully finishes"
                    property="downstreamTasks"
                    value={taskCfg?.downstreamTasks}
                    onChange={handleBasicInputChange}
                    dagId={taskCfg?.parentId!!}
                  />

                  <BooleanFieldInput
                    label="Skip"
                    info="Skip this task's execution but maintain task ordering and DAG structure"
                    property="isSkipped"
                    value={taskCfg?.isSkipped}
                    onChange={handleBasicInputChange}
                    widthMd={1}
                  />

                  {!isTemplateForced && (
                    <BooleanFieldInput
                      label="Use Template"
                      info="Use predefined input and output template"
                      property="templateInfo"
                      value={isTemplated}
                      onChange={toggleIsUserTemplated}
                      widthMd={2}
                    />
                  )}

                  {!isTemplateForced && (
                    <BooleanFieldInput
                      label="Override Recipients"
                      info="Override e-mail recipients with extra recipients"
                      property="overrideRecipients"
                      value={taskCfg?.overrideRecipients}
                      onChange={handleBasicInputChange}
                      widthMd={2}
                    />
                  )}

                  <BooleanFieldInput
                    label="Allow edit locked"
                    info="Allow editing locked fields"
                    property="allowEditLocked"
                    value={taskCfg?.allowEditLocked}
                    onChange={handleBasicInputChange}
                    widthMd={2}
                  />

                  {!isTemplated && (
                    <BooleanFieldInput
                      label="Use Categories"
                      info="Enable dataset category inputs"
                      property="useCategories"
                      value={taskCfg?.useCategories}
                      onChange={handleBasicInputChange}
                      widthMd={2}
                    />
                  )}

                  <div className={`col-${6}`} />

                  {taskCfg?.overrideRecipients && (
                    <ChipsFieldInput
                      label="Extra Recipients"
                      info="Extra execution report e-mail recipients"
                      property="extraExecutionRecipients"
                      value={taskCfg?.extraExecutionRecipients}
                      onChange={handleBasicInputChange}
                      widthMd={12}
                    />
                  )}

                  {!isTemplateForced && (
                    <CodeUploadInput
                      value={taskCfg?.codeFileId}
                      initialCode={taskCfg?.code}
                      codeB64={taskCfg?.codeB64}
                      initialFilename={taskCfg?.codeFileName}
                      onChange={handleCodeFileChange}
                    />
                  )}

                  <div className="col-12 mt-3">
                    <h4>Timing options</h4>
                    <hr />
                  </div>

                  <TimespanFieldInput
                    label="Lag Time"
                    info="Lag amount before time reference point"
                    property="dataLag"
                    propertyUnit="dataLagUnit"
                    value={taskCfg?.dataLag}
                    valueUnit={taskCfg?.dataLagUnit}
                    onChange={handleBasicInputChange}
                    widthMd={6}
                  />

                  <TimespanFieldInput
                    label="Lead Time"
                    info="Lead amount after time reference point"
                    property="dataLead"
                    propertyUnit="dataLeadUnit"
                    value={taskCfg?.dataLead}
                    valueUnit={taskCfg?.dataLeadUnit}
                    onChange={handleBasicInputChange}
                    widthMd={6}
                  />

                  <NumberFieldInput
                    label="Step count"
                    info="Steps calculation will execute. Steps start from calculation offset. Each step apply lag and lead times. Must be greater than 0"
                    property="calculationSteps"
                    value={taskCfg?.calculationSteps}
                    onChange={handleBasicInputChange}
                    widthMd={4}
                  />

                  <TimespanFieldInput
                    label="Step period"
                    info="Single calculation period step's 'width'"
                    property="calculationPeriod"
                    propertyUnit="calculationPeriodUnit"
                    value={taskCfg?.calculationPeriod}
                    valueUnit={taskCfg?.calculationPeriodUnit}
                    onChange={handleBasicInputChange}
                    widthMd={4}
                  />

                  <TimespanFieldInput
                    label="Calculation offset"
                    info="Offset from current time at which calculation periods starts"
                    property="calculationTimeOffset"
                    propertyUnit="calculationTimeOffsetUnit"
                    value={taskCfg?.calculationTimeOffset}
                    valueUnit={taskCfg?.calculationTimeOffsetUnit}
                    onChange={handleBasicInputChange}
                    widthMd={4}
                  />
                </div>
              </TabPanel>

              {!isTemplated && (
                <TabPanel header="Generic data" disabled={isTemplated} contentClassName="mt-3">
                  <TaskConfigConstantMapTable
                    property="constants"
                    constants={taskCfg?.constants}
                    onChange={handleBasicInputChange}
                  />
                  <TaskConfigProductTemplatesTable
                    header="Product templates"
                    productTemplates={taskCfg?.productTemplates}
                    onChange={handleBasicInputChange}
                    bodyClass="mt-5"
                  />

                  <div className="p-fluid grid formgrid mt-3">
                    <div className="col-12">
                      <h4>Freestyle JSON</h4>
                      <hr />
                    </div>

                    <div className="mx-2" style={{ width: "100%" }}>
                      <Editor
                        height="400px"
                        value={taskCfg?.freestyleJson || ""}
                        onChange={e => handleJsonEditorChange(e)}
                        defaultLanguage="json"
                        theme="vs-dark"
                      />
                    </div>
                  </div>
                </TabPanel>
              )}

              {!isTemplated && (
                <TabPanel header="Named datasets" disabled={isTemplated} contentClassName="mt-3">
                  <div className="p-fluid grid formgrid">
                    <div className="col-12">
                      <TaskConfigNamedDatasetTable
                        header="Input named datasets"
                        property="namedInputDatasets"
                        datasets={taskCfg?.namedInputDatasets!!}
                        onChange={handleBasicInputChange}
                      />
                      <TaskConfigNamedDatasetTable
                        header="Output named datasets"
                        property="namedOutputDatasets"
                        datasets={taskCfg?.namedOutputDatasets!!}
                        onChange={handleBasicInputChange}
                        bodyClass="mt-5"
                      />
                    </div>
                  </div>
                </TabPanel>
              )}

              {!isTemplated && (
                <TabPanel header="Indexed datasets" contentClassName="mt-3">
                  <div className="p-fluid grid formgrid">
                    <div className="col-12">
                      <TaskConfigIndexedDatasetTable
                        header="Input Indexed Datasets"
                        property="indexedInputDatasets"
                        datasets={taskCfg?.indexedInputDatasets!!}
                        onChange={handleBasicInputChange}
                      />
                      <TaskConfigIndexedDatasetTable
                        header="Output Indexed Datasets"
                        property="indexedOutputDatasets"
                        datasets={taskCfg?.indexedOutputDatasets!!}
                        onChange={handleBasicInputChange}
                        bodyClass="mt-5"
                      />
                    </div>
                  </div>
                </TabPanel>
              )}

              {taskCfg?.useCategories && !isTemplated && (
                <TabPanel header="Categories" disabled={isTemplated}>
                  <TaskConfigCategoryTable
                    header="Input categories"
                    property="inputCategories"
                    categories={taskCfg?.inputCategories!!}
                    onChange={handleBasicInputChange}
                  />
                  <TaskConfigCategoryTable
                    header="Output categories"
                    property="outputCategories"
                    categories={taskCfg?.outputCategories!!}
                    onChange={handleBasicInputChange}
                    bodyClass="mt-5 mb-5"
                  />

                  <div className="p-fluid grid formgrid">
                    <div className="col-12">
                      <h4>Timing Options</h4>
                      <hr />
                    </div>

                    <TimespanFieldInput
                      label="Category Lead Time"
                      info="Lead amount after time reference point. Applies only for datasets from categories selection"
                      property="categoryDataLead"
                      propertyUnit="categoryDataLeadUnit"
                      value={taskCfg?.categoryDataLead}
                      valueUnit={taskCfg?.categoryDataLeadUnit}
                      onChange={handleBasicInputChange}
                      widthMd={6}
                    />

                    <TimespanFieldInput
                      label="Category Lag Time"
                      info="Lag amount after time reference point. Applies only for datasets from categories selection"
                      property="categoryDataLag"
                      propertyUnit="categoryDataLagUnit"
                      value={taskCfg?.categoryDataLag}
                      valueUnit={taskCfg?.categoryDataLagUnit}
                      onChange={handleBasicInputChange}
                      widthMd={6}
                    />
                  </div>
                </TabPanel>
              )}

              {/* Render templated tabs */}
              {isTemplated && renderTemplates(taskCfg, handleTemplateGroupsChange)}

              <TabPanel header="Task config JSON" contentClassName="mt-3">
                <TaskCfgJSONViewer taskCfg={taskCfg!!} />
              </TabPanel>

              <TabPanel header="Template editor" contentClassName="mt-3">
                <TaskTemplateDefinitionEditor taskCfg={taskCfg} onChange={e => setDefinition(e)} />
              </TabPanel>
            </TabView>
          )}
        </div>

        <div className="flex">
          <Button
            label="Save"
            className="flex-grow-1 m-2 p-button-success"
            icon="pi pi-save"
            iconPos="right"
            onClick={onClickSave}
            disabled={saving}
          />
          <Button
            label="Apply"
            className="flex-grow-1 m-2 p-button-info"
            icon="pi pi-check"
            iconPos="right"
            onClick={onClickApply}
            disabled={saving}
          />
          <Button
            label="Apply & Run"
            className="flex-grow-1 m-2 p-button-warning"
            icon="pi pi-play"
            iconPos="right"
            onClick={onClickRun}
            disabled={saving}
          />
          <Button
            label="Cancel"
            className="flex-none m-2 p-button-danger"
            icon="pi pi-times"
            onClick={onClickGoBack}
            disabled={saving}
          />
        </div>
      </div>

      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
        header="Create new dataset"
        visible={createDatasetDialogVisibility}
        onHide={() => setCreateDialogVisibility(false)}
        resizable
      >
        <CreateDatasetDialog setCreateDialogVisibility={setCreateDialogVisibility} />
      </Dialog>
      <Dialog
        header="Rollback to..."
        visible={showRollbackDialog}
        style={{ width: "min(450px, 50vw)" }}
        footer={rollbackDialogFooter}
        onHide={() => setShowRollbackDialog(false)}
      >
        <div className="p-fluid grid formgrid">
          <div className="field col-12">
            <ScheduledTaskHistoryTable
              value={selectedTaskHistory}
              dagId={taskCfg?.parentId!!}
              taskId={taskCfg?.id!!}
              onChange={e => setSelectedTaskHistory(e.value)}
              currentConfig={JSON.stringify(taskCfg)}
            />
          </div>
        </div>
      </Dialog>

      <TemplateInfoDialog
        taskCfg={taskCfg!!}
        visible={showTemplateInfoDialog}
        setVisible={setShowTemplateInfoDialog}
      />
      <TemplateUpdateDialog
        taskCfg={taskCfg!!}
        visible={showTemplateUpdateDialog}
        setVisible={setShowTemplateUpdateDialog}
        onUpdate={() => null}
      />
    </EasasPageTemplateWrapper>
  );
}
function TaskTemplateDefinitionEditor(props: {
  taskCfg: ScheduledTaskDTO;
  onChange?: (e: any) => void;
}) {
  const [def, setDef] = useState<string | undefined>(
    JSON.stringify(props.taskCfg.templateInfo.definition, null, 2)
  );
  const handleDefChange = (newDef: any) => {
    try {
      const val = JSON.parse(newDef);
      props.onChange && props.onChange(val);
      easasAlert({
        summary: "Success",
        detail: "Template definition applied",
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      easasAlert({
        summary: "Error",
        detail: "Invalid template definition json",
        severity: "error",
      });
    }
  };

  return (
    <div className="p-fluid grid formgrid">
      <div className="field col-12">
        <TabView activeIndex={0}>
          <TabPanel header="Basic editor">
            <TemplateEditor
              initialData={def}
              handleDefChange={e => {
                const newDef = JSON.stringify(e, null, 2);
                setDef(newDef);
                handleDefChange(newDef);
              }}
            />
          </TabPanel>
          <TabPanel header="JSON editor">
            <TaskTemplateDefinitionEditorJSON
              taskCfg={props.taskCfg}
              onChange={e => {
                setDef(JSON.stringify(e, null, 2));
                handleDefChange(JSON.stringify(e, null, 2));
              }}
            />
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
}

function TaskTemplateDefinitionEditorJSON(props: {
  taskCfg: ScheduledTaskDTO;
  onChange?: (e: any) => void;
}) {
  const [def, setDef] = useState<string | undefined>(
    JSON.stringify(props.taskCfg.templateInfo.definition, null, 2)
  );

  console.log("Render [TaskTemplateDefDEBUG]");

  const handleDefChange = () => {
    try {
      const val = JSON.parse(def!!);
      props.onChange!!(val);
      easasAlert({
        summary: "Success",
        detail: "Template definition applied",
        severity: "success",
      });
    } catch (e) {
      console.error(e);
      easasAlert({
        summary: "Error",
        detail: "Invalid template definition json",
        severity: "error",
      });
    }
  };

  return (
    <div className="p-fluid grid formgrid">
      <div className="field col-12">
        <Editor
          height="650px"
          defaultLanguage="json"
          value={def}
          onChange={e => setDef(e)}
          theme="vs-dark"
        />
      </div>

      <div className="field col-12">
        <Button
          label="Apply template"
          className="p-button-success"
          icon="pi pi-check"
          onClick={handleDefChange}
        />
      </div>
    </div>
  );
}

function TaskCfgJSONViewer(props: { taskCfg: ScheduledTaskDTO }) {
  const cfg = { ...props.taskCfg };
  // cfg.templateInfo.definition = undefined;
  cfg.code = undefined;

  console.log("Render [TaskCfgJSONViewer]");

  return (
    <div>
      <code style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(cfg, null, 2)}</code>
    </div>
  );
}

interface ITemplatedTaskTabContentProps {
  groupDef: TaskTemplateInputGroupDefinition;
  groups: TaskInputGroup[];
  taskCfg: ScheduledTaskDTO;
  onChange?: (e: TaskInputGroup[]) => void;
}

const renderTemplates = (
  taskCfg: ScheduledTaskDTO,
  onGroupsChanged?: (e: Record<string, TaskInputGroup[]>) => void
) => {
  const defs = taskCfg?.templateInfo.definition?.inputGroupDefinitions;

  const onChange = (groupName: string, groups: TaskInputGroup[]) => {
    const updated = taskCfg.inputGroups!!;
    updated[groupName] = groups;
    onGroupsChanged!!(updated);
  };

  return (
    defs &&
    defs.map(groupDef => {
      const groups = taskCfg.inputGroups[groupDef.id] ?? [];

      return (
        <TabPanel key={groupDef.id} header={groupDef.header}>
          <TemplatedTaskTabContent
            groupDef={groupDef}
            groups={groups}
            taskCfg={taskCfg}
            onChange={groups => onChange(groupDef.id, groups)}
          />
        </TabPanel>
      );
    })
  );
};

const TemplatedTaskTabContent = (props: ITemplatedTaskTabContentProps) => {
  const [tabIndex, setTabIndex] = useState(0);

  const def = props.groupDef;
  const { subForms } = def;
  const { groups } = props;

  const tabIndexChangeHandler = (title: string) => {
    setTabIndex(groups.map(e => e.title).indexOf(title) + 1);
  };

  const onChange = (
    inputType: TaskTemplateInputType,
    subgroupIndex: number,
    e: InputChangeEvent<any>
  ) => {
    console.log("Changed templated input field", e);

    const prop = e.property as keyof TaskInputGroup;
    const valueLoc = TaskInputGroup.getValueLocation(inputType);
    if (!valueLoc) return;

    let subgroup = groups[subgroupIndex];

    // Make sure to initialize subgroup object if it doesn't exist
    if (!subgroup) {
      subgroup = new TaskInputGroup({
        isGrouped: def.type === TaskTemplateInputGroupType.GROUPED,
      });
      groups[subgroupIndex] = subgroup;
    }

    // Initialize task group map object if it doesn't exist
    if (subgroup[valueLoc] == null) {
      (subgroup[valueLoc] as Record<string, any>) = {};
    }

    (subgroup[valueLoc] as Record<string, any>)[prop] = e.value;

    props.onChange!!(groups);
  };

  const onGroupsChange = (e: InputChangeEvent<TaskInputGroup[]>) => {
    console.log("Changed groups", e);
    props.onChange!!(e.value);
  };

  /**
   * Get value of input field from group structure
   * @param inputName Reference name of input field
   * @param input Input field definition
   * @param subgroupIndex index of subgroup, 0 if not grouped
   */
  const getFieldValue = (
    inputName: string,
    input: ITaskTemplateInputDefinition,
    subgroupIndex: number = 0
  ) => {
    const valueLoc = TaskInputGroup.getValueLocation(input.type);
    if (!valueLoc) return undefined;

    const subgroup = groups[subgroupIndex];

    if (!subgroup) return null; // No subgroup, no value

    const inputTypeGroup = subgroup[valueLoc] as Record<string, any>; // Get input type group (e.g. "constants") input map
    if (!inputTypeGroup) return null;

    return inputTypeGroup[inputName];
  };

  const groupForm = (subgroupIndex: number = 0) => {
    return (
      subForms?.length &&
      subForms.map((subForm, subFormIdx) => {
        return (
          <div className="p-fluid grid formgrid" key={subFormIdx}>
            {/* ======= SubForm Header ====== */}
            <div
              style={{
                marginTop: def.type === TaskTemplateInputGroupType.SIMPLE ? "1rem" : "",
              }}
              className="col-12"
            >
              {subForm.header && <h4>{subForm.header}</h4>}
              <hr />
            </div>

            {/* ======= Group form input mapping ====== */}
            {subForm?.inputs &&
              subForm?.inputs.map(input => {
                return (
                  <TemplatedTaskInputField
                    key={input.id}
                    inputName={input.id}
                    input={input}
                    value={getFieldValue(input.id, input, subgroupIndex)}
                    onChange={e => onChange(input.type, subgroupIndex, e)}
                    allowEditLocked={props.taskCfg.allowEditLocked}
                  />
                );
              })}
          </div>
        );
      })
    );
  };

  return (
    <div>
      {def.type == TaskTemplateInputGroupType.GROUPED && (
        <TabView scrollable activeIndex={tabIndex} onTabChange={e => setTabIndex(e.index)}>
          <TabPanel leftIcon="pi pi-cog">
            <TaskTemplatesSubgroupManagerTable
              subgroups={groups}
              onChange={onGroupsChange}
              changeTab={tabIndexChangeHandler}
            />
          </TabPanel>

          {groups.map((group, subgroupIndex) => {
            return (
              <TabPanel key={group.title} header={group.title}>
                {groupForm(subgroupIndex)}
              </TabPanel>
            );
          })}
        </TabView>
      )}

      {def.type == TaskTemplateInputGroupType.SIMPLE && groupForm()}
    </div>
  );
};

interface ITemplatedTaskInputFieldProps {
  inputName: string;
  input: ITaskTemplateInputDefinition<any>;
  value: any | null;
  onChange: (e: InputChangeEvent<any>) => void;
  allowEditLocked: boolean;
}

interface BaseChangeProps<T, KT> {
  property: KT;
  sourceObj: T | undefined;
  onChange: (prop: KT, value: T) => void;
}

function TemplatedTaskInputField(props: ITemplatedTaskInputFieldProps) {
  const { input } = props;

  console.log(`Render [TemplatedTaskInputField: ${props.input.type}]`);
  const onChange = (e: InputChangeEvent<any>) => {
    console.log("TemplatedTaskInputField change", e);
    props.onChange!!(e);
  };

  const commonProps = {
    label: input.title!!,
    info: input.info,
    widthMd: input.width || 12,
    property: props.inputName,
    onChange,
    value: props.value,
    allowEditLocked: props.allowEditLocked,
  } as IFieldInputProps;

  return (
    <>
      {/* ========================= Constants ========================= */}
      {input.type === TaskTemplateInputType.CONSTANT && (
        <TaskConfigConstantField {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Dataset Pairs ========================= */}
      {input.type === TaskTemplateInputType.DATASET_PAIRS && (
        <TaskConfigDatasetPairTable {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Indexed datasets ========================= */}
      {(input.type === TaskTemplateInputType.INPUT_INDEXED_DATASETS ||
        input.type === TaskTemplateInputType.OUTPUT_INDEXED_DATASETS) && (
        <DatasetMultiSelectFieldInput {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Named datasets ========================= */}
      {(input.type === TaskTemplateInputType.INPUT_NAMED_DATASET ||
        input.type === TaskTemplateInputType.OUTPUT_NAMED_DATASET) && (
        <DatasetDropdownFieldInput {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Named Categories ========================= */}
      {(input.type === TaskTemplateInputType.INPUT_NAMED_CATEGORY ||
        input.type === TaskTemplateInputType.OUTPUT_NAMED_CATEGORY) && (
        <DatasetCategoryDropdownField {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Product Templates ========================= */}
      {input.type === TaskTemplateInputType.PRODUCT_TEMPLATE && (
        <TaskConfigProductTemplateField {...commonProps} fieldOptions={input.options} />
      )}

      {/* ========================= Freestyle JSON ========================= */}
      {input.type === TaskTemplateInputType.FREESTYLE_JSON && (
        <TaskConfigFreestyleJsonField {...commonProps} fieldOptions={input.options} />
      )}
      {/* ========================= Comment ================================ */}
      {input.type === TaskTemplateInputType.COMMENT && <InputComment {...props.input} />}

      {/* ========================= Invalid type warning ========================= */}
      {!Object.values(TaskTemplateInputType).includes(input.type) && (
        <div className={`field col-${input.width}`}>
          <InfoLabel info={input.info}>{input.title}</InfoLabel>
          <div style={{ background: "orangered", color: "ivory", padding: "4px" }}>
            Invalid input type: {input.type}
          </div>
        </div>
      )}
    </>
  );
}
